<template>
  <div class="purchase-search-form">
    <div class="search-row">
      <div class="search-item" v-if="showTotalCount">
        <span class="label">{{ totalCountLabel }}：</span>
        <span class="value">{{ totalCount }}</span>
      </div>
      
      <!-- 动态渲染搜索项 -->
      <template v-for="field in searchFields" :key="field.prop">
        <div class="search-item">
          <!-- 输入框 -->
          <template v-if="field.type === 'input'">
            <span class="label" v-if="field.label">{{ field.label }}</span>
            <el-input 
              v-model="formData[field.prop]" 
              :placeholder="field.placeholder || (field.prop === 'wd' ? '请输入厂家、系列、型号、订货号...查价格' : '请输入')" 
              size="small"
              clearable
              :style="{ width: field.width || '180px' }"
            />
          </template>
          
          <!-- 下拉选择 -->
          <template v-else-if="field.type === 'select'">
            <span class="label" v-if="field.label">{{ field.label }}</span>
            <el-select 
              v-model="formData[field.prop]"
              :placeholder="field.placeholder"
              size="small"
              clearable
              :style="{ width: field.width || '180px' }"
            >
              <el-option 
                v-for="option in field.options" 
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </template>
          
          <!-- 日期选择 -->
          <template v-else-if="field.type === 'date'">
            <span class="label" v-if="field.label">{{ field.label }}</span>
            <el-date-picker
              v-model="formData[field.prop]"
              type="date"
              :placeholder="field.placeholder"
              size="small"
              :style="{ width: field.width || '140px' }"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
            />
          </template>
          
          <!-- 日期范围 -->
          <el-date-picker
            v-else-if="field.type === 'daterange'"
            v-model="formData[field.prop]"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            size="small"
            :style="{ width: field.width || '240px' }"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
          />
        </div>
      </template>
      
      <!-- 时间快捷筛选 -->
      <div class="search-item time-filter-tabs" v-if="showTimeFilter">
        <div 
          v-for="timeTab in timeFilterTabs" 
          :key="timeTab.value"
          :class="['time-tab', { active: activeTimeFilter === timeTab.value }]"
          @click="handleTimeFilterClick(timeTab.value)"
        >
          {{ timeTab.label }}
        </div>
      </div>
      
      <div class="search-item">
        <el-button type="primary" size="small" @click="handleSearch" class="search-btn">查询</el-button>
        <el-button size="small" @click="handleReset" class="reset-btn">重置</el-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PurchaseSearchForm',
  props: {
    totalCount: {
      type: Number,
      default: 0
    },
    totalCountLabel: {
      type: String,
      default: '总采购单数'
    },
    showTotalCount: {
      type: Boolean,
      default: true
    },
    showTimeFilter: {
      type: Boolean,
      default: true
    },
    searchFields: {
      type: Array,
      default: () => []
    },
    timeFilterTabs: {
      type: Array,
      default: () => [
        { label: '今天', value: 'today' },
        { label: '本周', value: 'week' },
        { label: '本月', value: 'month' },
      ]
    },
    modelValue: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      formData: {},
      activeTimeFilter: '',
    };
  },
  created() {
    this.initFormData();
  },
  methods: {
    initFormData() {
      const data = {};
      this.searchFields.forEach(field => {
        data[field.prop] = '';
      });
      this.formData = { ...data, ...this.modelValue };
    },
    handleTimeFilterClick(value) {
      this.activeTimeFilter = value;
      const today = new Date();
      
      if (value === 'today') {
        const dateStr = this.formatDate(today);
        this.formData.startDate = dateStr;
        this.formData.endDate = dateStr;
      } else if (value === 'week') {
        const dayOfWeek = today.getDay();
        const monday = new Date(today);
        monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        
        this.formData.startDate = this.formatDate(monday);
        this.formData.endDate = this.formatDate(sunday);
      } else if (value === 'month') {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        
        this.formData.startDate = this.formatDate(firstDay);
        this.formData.endDate = this.formatDate(lastDay);
      }
      
      this.handleSearch();
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    handleSearch() {
      this.$emit('search', { ...this.formData });
    },
    handleReset() {
      this.initFormData();
      this.activeTimeFilter = '';
      // 确保日期字段也被清空
      this.formData.startDate = '';
      this.formData.endDate = '';
      this.$emit('reset', { ...this.formData });
    }
  },
  watch: {
    modelValue: {
      handler(val) {
        if (val) {
          this.formData = { ...this.formData, ...val };
        }
      },
      immediate: true,
      deep: true
    }
  }
};
</script>

<style scoped lang="less">
.purchase-search-form {
  padding: 20px;
  padding-bottom: 0;
  .search-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
    
    .search-item {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .label {
        font-size: 14px;
        color: #606266;
        white-space: nowrap;
      }
      
      .value {
        font-size: 14px;
        color: #EB4949;
        font-weight: 500;
      }
      
      &.time-filter-tabs {
        display: flex;
        gap: 2px;
        border-bottom: 1px solid #DCDFE6;
        
        .time-tab {
          padding: 6px 20px;
          border: 1px solid #DCDFE6;
          border-bottom: none;
          cursor: pointer;
          font-size: 14px;
          color: #909399;
          background: #F5F7FA;
          transition: color 0.3s, background 0.3s;
          position: relative;
          
          &:first-child {
            border-radius: 4px 0 0 0;
          }
          
          &:last-child {
            border-radius: 0 4px 0 0;
          }
          
          &:hover {
            color: #48A09C;
          }
          
          &.active {
            color: #48A09C;
            background: #fff;
            border-color: rgba(0,0,0,0.15);
            border-bottom: 1px solid #fff;
            margin-bottom: -1px;
            z-index: 1;
          }
        }
      }
    }
  }
  
  .search-btn {
    background: #48A09C !important;
    border-color: #48A09C !important;
    color: #fff !important;
    padding: 7px 20px !important;
    font-size: 14px !important;
    
    &:hover {
      background: #3d8a86 !important;
      border-color: #3d8a86 !important;
    }
  }
  
  .reset-btn {
    background: #fff !important;
    border: 1px solid #48A09C !important;
    color: #48A09C !important;
    padding: 7px 20px !important;
    font-size: 14px !important;
    
    &:hover {
      background: #f0f9f8 !important;
    }
  }
}
</style>
